/**
 * AddColumnModal - modal for adding a new column to the board
 */

import { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { clsx } from 'clsx';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useBoardStore } from '@stores/boardStore';
import { toast } from '@components/ui/Toast';

interface AddColumnModalProps {
  boardId: string;
  open: boolean;
  onClose: () => void;
}

const colorOptions = [
  { value: '#6B7280', label: 'Gray' },
  { value: '#EF4444', label: 'Red' },
  { value: '#F97316', label: 'Orange' },
  { value: '#F59E0B', label: 'Amber' },
  { value: '#EAB308', label: 'Yellow' },
  { value: '#84CC16', label: 'Lime' },
  { value: '#22C55E', label: 'Green' },
  { value: '#14B8A6', label: 'Teal' },
  { value: '#06B6D4', label: 'Cyan' },
  { value: '#3B82F6', label: 'Blue' },
  { value: '#6366F1', label: 'Indigo' },
  { value: '#8B5CF6', label: 'Violet' },
  { value: '#A855F7', label: 'Purple' },
  { value: '#EC4899', label: 'Pink' },
];

export default function AddColumnModal({
  boardId,
  open,
  onClose,
}: AddColumnModalProps) {
  const { t } = useTranslation();
  const { addColumn } = useBoardStore();

  const [name, setName] = useState('');
  const [color, setColor] = useState<string>('#3B82F6');
  const [wipLimit, setWipLimit] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = () => {
    setName('');
    setColor('#3B82F6');
    setWipLimit('');
    onClose();
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    setIsSaving(true);
    try {
      await addColumn(boardId, {
        name: name.trim(),
      });

      toast.success(t('board.columnAdded'));
      handleClose();
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t('board.failedAddColumn');
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <Dialog.Portal>
        <Dialog.Overlay
          className={clsx(
            'fixed inset-0 z-50 bg-black/50',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0'
          )}
        />
        <Dialog.Content
          className={clsx(
            'fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2',
            'rounded-lg bg-white p-6 shadow-xl dark:bg-neutral-800',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95'
          )}
        >
          {/* Header */}
          <div className="mb-6 flex items-center justify-between">
            <Dialog.Title className="text-lg font-semibold text-neutral-900 dark:text-white">
              {t('board.addColumnTitle')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className={clsx(
                  'rounded p-1 transition-colors',
                  'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700',
                  'dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                )}
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Form */}
          <div className="space-y-4">
            {/* Name */}
            <div>
              <label
                htmlFor="new-column-name"
                className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('common.name', 'Name')}
              </label>
              <input
                id="new-column-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && name.trim()) {
                    void handleSave();
                  }
                }}
                autoFocus
                className={clsx(
                  'w-full rounded-md border px-3 py-2',
                  'border-neutral-300 bg-white text-neutral-900',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                )}
                placeholder={t('board.addColumnNamePlaceholder')}
              />
            </div>

            {/* Color */}
            <div>
              <label className="mb-2 block text-sm font-medium text-neutral-700 dark:text-neutral-300">
                {t('board.colorLabel')}
              </label>
              <div className="flex flex-wrap gap-2">
                {colorOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setColor(opt.value)}
                    className={clsx(
                      'h-8 w-8 rounded-full transition-transform',
                      'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2',
                      color === opt.value && 'ring-2 ring-primary-500 ring-offset-2'
                    )}
                    style={{ backgroundColor: opt.value }}
                    aria-label={opt.label}
                    aria-pressed={color === opt.value}
                    title={opt.label}
                  />
                ))}
              </div>
            </div>

            {/* WIP Limit */}
            <div>
              <label
                htmlFor="new-wip-limit"
                className="mb-1 block text-sm font-medium text-neutral-700 dark:text-neutral-300"
              >
                {t('board.wipLimitOptional')}
              </label>
              <input
                id="new-wip-limit"
                type="number"
                min="0"
                value={wipLimit}
                onChange={(e) => setWipLimit(e.target.value)}
                className={clsx(
                  'w-full rounded-md border px-3 py-2',
                  'border-neutral-300 bg-white text-neutral-900',
                  'focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500',
                  'dark:border-neutral-600 dark:bg-neutral-700 dark:text-white'
                )}
                placeholder={t('board.wipLimitPlaceholder')}
              />
              <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                {t('board.wipLimitHelpAdd')}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-neutral-100 text-neutral-700 hover:bg-neutral-200',
                'dark:bg-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-600'
              )}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving || !name.trim()}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                'bg-primary-600 text-white hover:bg-primary-700',
                'disabled:cursor-not-allowed disabled:opacity-50'
              )}
            >
              {isSaving ? t('board.addingColumn') : t('board.addColumnTitle')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
